import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  UnfoldMore as FullscreenIcon,
  UnfoldLess as ExitFullscreenIcon,
} from '@mui/icons-material';

/**
 * FlexOutputConsole - Bottom panel showing execution results
 *
 * Features:
 * - Execution output display
 * - Success/error status with timing
 * - Trace output when enabled
 * - Collapsible panel
 */
function FlexOutputConsole({
  output = null,
  isCollapsed = false,
  onToggleCollapse,
  onClear,
  onGoToLine, // Callback: (line, column) => void — navigate editor to line
  embedded = false, // When true, renders without collapse header for right panel
  traceEnabled = false, // When true (Enable Trace toggle is on), show a Trace section even with 0 steps
}) {
  const outputRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Toggle fullscreen mode
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setIsFullscreen(!isFullscreen);
    // Expand panel when going fullscreen
    if (!isFullscreen && isCollapsed) {
      onToggleCollapse();
    }
  };

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    if (isFullscreen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen]);

  // Auto-scroll to bottom when output changes
  useEffect(() => {
    if (outputRef.current && output) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Render status icon
  const renderStatusIcon = () => {
    if (!output) return null;

    if (output.success) {
      return <SuccessIcon sx={{ color: 'success.main', fontSize: 18 }} />;
    } else if (output.diagnostics?.some((d) => d.severity === 'error')) {
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    } else if (output.diagnostics?.some((d) => d.severity === 'warning')) {
      return <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
    }
    return <InfoIcon sx={{ color: 'info.main', fontSize: 18 }} />;
  };

  // Format output value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  // Render output content (shared between embedded and normal modes)
  const renderOutputContent = () => (
    <Box
      ref={outputRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'auto',
        p: 1.5,
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        bgcolor: 'background.default',
      }}
    >
      {!output ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: 11 }}>
          Run a script to see output here...
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Diagnostics (errors/warnings) */}
          {output.diagnostics && output.diagnostics.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {output.diagnostics.map((diag, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    if (diag.line && onGoToLine) {
                      onGoToLine(diag.line, diag.column || 1);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    color: diag.severity === 'error'
                      ? 'error.main'
                      : diag.severity === 'warning'
                        ? 'warning.main'
                        : 'info.main',
                    mb: 0.5,
                    cursor: diag.line ? 'pointer' : 'default',
                    borderRadius: 0.5,
                    px: 0.5,
                    mx: -0.5,
                    '&:hover': diag.line ? {
                      bgcolor: 'action.hover',
                    } : {},
                  }}
                >
                  {diag.severity === 'error' ? (
                    <ErrorIcon sx={{ fontSize: 14, mt: 0.3 }} />
                  ) : diag.severity === 'warning' ? (
                    <WarningIcon sx={{ fontSize: 14, mt: 0.3 }} />
                  ) : (
                    <InfoIcon sx={{ fontSize: 14, mt: 0.3 }} />
                  )}
                  <Typography variant="body2" sx={{ fontSize: 11 }}>
                    {diag.line && <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>{`Line ${diag.line}: `}</span>}
                    {diag.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Result */}
          {output.success && output.result !== undefined && (
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: 10, mb: 0.5 }}
              >
                Result:
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 11,
                }}
              >
                {formatValue(output.result)}
              </Box>
            </Box>
          )}

          {/* Trace output -- also shown (empty) when Enable Trace is on but no steps came back, so the
              feature stays discoverable instead of silently showing nothing. */}
          {((output.trace && output.trace.length > 0) || traceEnabled) && (
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: 10, mb: 0.5 }}
              >
                Trace:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {output.trace && output.trace.length > 0 ? (
                  output.trace.map((step, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        fontSize: 10,
                        color: step.type === 'error' ? 'error.main' : 'text.primary',
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}
                    >
                      [{step.timestamp || index}] {step.message}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ fontSize: 10, fontStyle: 'italic', color: 'text.secondary' }}>
                    No trace steps recorded for this run.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Variables / Output Context */}
          {output.variables && Object.keys(output.variables).length > 0 && (
            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: 10, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Output Context
                <Chip label={`${Object.keys(output.variables).length}`} size="small" sx={{ height: 14, fontSize: 9 }} />
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {Object.entries(output.variables).map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 0.5,
                      pb: 0.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0, mb: 0, pb: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'primary.main',
                        minWidth: 100,
                      }}
                    >
                      {key}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        color: 'text.primary',
                        bgcolor: 'transparent',
                        wordBreak: 'break-all',
                        whiteSpace: 'pre-wrap',
                        m: 0,
                        p: 0,
                        flex: 1,
                      }}
                    >
                      {formatValue(value)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // Embedded mode - render with simple header
  if (embedded) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Simple header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, flex: 1 }}>
            Output
          </Typography>
          {output && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {renderStatusIcon()}
              {output.processingTimeMs && (
                <Chip
                  label={`${output.processingTimeMs}ms`}
                  size="small"
                  sx={{ height: 18, fontSize: 9 }}
                />
              )}
            </Box>
          )}
          <Tooltip title="Clear output">
            <IconButton
              size="small"
              onClick={onClear}
              sx={{ opacity: output ? 1 : 0.3, p: 0.25 }}
              disabled={!output}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {renderOutputContent()}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        // Fullscreen styles
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          borderTop: 0,
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          borderBottom: isCollapsed ? 0 : 1,
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleCollapse}
      >
        <IconButton size="small" sx={{ p: 0.5 }}>
          {isCollapsed ? (
            <ExpandIcon fontSize="small" />
          ) : (
            <CollapseIcon fontSize="small" />
          )}
        </IconButton>

        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          Output
        </Typography>

        {output && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {renderStatusIcon()}
            {output.processingTimeMs && (
              <Chip
                label={`${output.processingTimeMs}ms`}
                size="small"
                sx={{ height: 20, fontSize: 10 }}
              />
            )}
          </Box>
        )}

        <Tooltip title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}>
          <IconButton
            size="small"
            onClick={toggleFullscreen}
            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
          >
            {isFullscreen ? (
              <ExitFullscreenIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Clear output">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            sx={{ opacity: output ? 1 : 0.3 }}
            disabled={!output}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Output content */}
      <Collapse
        in={!isCollapsed || isFullscreen}
        sx={{
          flex: isFullscreen ? 1 : 'none',
          display: isFullscreen ? 'flex' : 'block',
          flexDirection: 'column',
          overflow: isFullscreen ? 'hidden' : 'visible',
          minHeight: 0,
        }}
      >
        <Box
          ref={outputRef}
          sx={{
            height: isFullscreen ? 'calc(100vh - 50px)' : 150,
            flex: isFullscreen ? 1 : 'none',
            overflowY: 'auto',
            overflowX: 'auto',
            p: 2,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: isFullscreen ? 13 : 12,
            bgcolor: 'background.default',
          }}
        >
          {!output ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Run a script to see output here...
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Diagnostics (errors/warnings) */}
              {output.diagnostics && output.diagnostics.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {output.diagnostics.map((diag, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        color: diag.severity === 'error'
                          ? 'error.main'
                          : diag.severity === 'warning'
                            ? 'warning.main'
                            : 'info.main',
                        mb: 0.5,
                      }}
                    >
                      {diag.severity === 'error' ? (
                        <ErrorIcon sx={{ fontSize: 14, mt: 0.3 }} />
                      ) : diag.severity === 'warning' ? (
                        <WarningIcon sx={{ fontSize: 14, mt: 0.3 }} />
                      ) : (
                        <InfoIcon sx={{ fontSize: 14, mt: 0.3 }} />
                      )}
                      <Typography variant="body2" sx={{ fontSize: 12 }}>
                        {diag.line && `Line ${diag.line}: `}
                        {diag.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Result */}
              {output.success && output.result !== undefined && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: isFullscreen ? 13 : 11, mb: 0.5 }}
                  >
                    Result:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: isFullscreen ? 2 : 1,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: isFullscreen ? 14 : 12,
                    }}
                  >
                    {formatValue(output.result)}
                  </Box>
                </Box>
              )}

              {/* Trace output */}
              {output.trace && output.trace.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: 11, mb: 0.5 }}
                  >
                    Trace:
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {output.trace.map((step, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          fontSize: 11,
                          color: step.type === 'error' ? 'error.main' : 'text.primary',
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        [{step.timestamp || index}] {step.message}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Variables / Output Context */}
              {output.variables && Object.keys(output.variables).length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: isFullscreen ? 13 : 11, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    Output Context
                    <Chip label={`${Object.keys(output.variables).length} vars`} size="small" sx={{ height: 16, fontSize: 9 }} />
                  </Typography>
                  <Box
                    sx={{
                      p: isFullscreen ? 2 : 1,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {Object.entries(output.variables).map(([key, value]) => (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          gap: isFullscreen ? 2 : 1,
                          mb: isFullscreen ? 1 : 0.5,
                          pb: isFullscreen ? 1 : 0.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 0, mb: 0, pb: 0 },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: isFullscreen ? 14 : 11,
                            fontWeight: 600,
                            color: 'primary.main',
                            minWidth: isFullscreen ? 180 : 120,
                          }}
                        >
                          {key}
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: isFullscreen ? 14 : 11,
                            color: 'text.primary',
                            bgcolor: 'transparent',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                            m: 0,
                            p: 0,
                            flex: 1,
                          }}
                        >
                          {formatValue(value)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default FlexOutputConsole;
